import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import type { AdCreativeProviderMediaReference } from '../../services/AdCreativeApiClient';
import { AD_CREATIVE_PROVIDER_SOURCES as PROVIDER_SOURCES } from '../../selectors';

interface ProviderSourceMediaGalleryProps {
  mediaRefs: AdCreativeProviderMediaReference[];
}

export function ProviderSourceMediaGallery({
  mediaRefs,
}: ProviderSourceMediaGalleryProps): React.ReactElement {
  const mirrored = mediaRefs.filter((media) => (
    media.retrievalState === 'mirrored' && Boolean(media.publicUrl)
  ));
  const unavailable = mediaRefs.filter((media) => (
    media.retrievalState !== 'mirrored' || !media.publicUrl
  ));
  const mirroredVideos = mirrored.filter((media) => media.kind === 'video').length;
  const referencedVideos = mediaRefs.filter((media) => media.kind === 'video').length;

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid={PROVIDER_SOURCES.mediaGallery}>
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'center' }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Media downloaded from Meta
            </Typography>
            <Typography variant="caption" color="text.secondary">
              These are the exact renditions Meta allowed Segmently to copy. They may be processed previews,
              not the advertiser upload masters.
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              color={unavailable.length === 0 ? 'success' : 'warning'}
              label={`${mirrored.length} of ${mediaRefs.length} files downloaded`}
            />
            {referencedVideos > 0 && (
              <Chip
                size="small"
                color={mirroredVideos === referencedVideos ? 'success' : 'warning'}
                label={`${mirroredVideos} of ${referencedVideos} videos playable`}
              />
            )}
          </Stack>
        </Stack>

        {mirrored.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
              gap: 1,
            }}
          >
            {mirrored.map((media, index) => (
              <Box
                key={media.id}
                sx={{ overflow: 'hidden', border: 1, borderColor: 'divider', borderRadius: 1.5 }}
                data-testid={PROVIDER_SOURCES.mediaGalleryItem(index)}
              >
                {media.kind === 'video' ? (
                  <Box
                    component="video"
                    src={media.publicUrl}
                    controls
                    playsInline
                    preload="metadata"
                    sx={{ display: 'block', width: '100%', aspectRatio: '16 / 9', objectFit: 'contain', bgcolor: 'common.black' }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={media.publicUrl}
                    alt={media.kind === 'poster' ? `Meta video poster ${index + 1}` : `Meta creative image ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                    sx={{ display: 'block', width: '100%', aspectRatio: '16 / 9', objectFit: 'contain', bgcolor: 'action.hover' }}
                  />
                )}
                <Stack spacing={0.25} sx={{ p: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {mediaLabel(media)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {mediaFacts(media)}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Box>
        ) : (
          <Alert severity="warning">
            Meta returned media references, but no playable image or video file was available to download.
          </Alert>
        )}

        {unavailable.length > 0 && (
          <Alert severity="warning">
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {`${unavailable.length} referenced ${unavailable.length === 1 ? 'file is' : 'files are'} unavailable`}
            </Typography>
            <Stack spacing={0.5}>
              {unavailable.map((media, index) => (
                <Typography
                  key={media.id}
                  variant="caption"
                  data-testid={PROVIDER_SOURCES.mediaGalleryUnavailable(index)}
                >
                  {`${mediaLabel(media)}: ${mediaFailureMessage(media)}`}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}

function mediaLabel(media: AdCreativeProviderMediaReference): string {
  if (media.kind === 'video') return 'Video rendition';
  if (media.kind === 'poster') return 'Video poster preview';
  return 'Image rendition';
}

function mediaFacts(media: AdCreativeProviderMediaReference): string {
  const facts = [
    media.mime,
    media.width && media.height ? `${media.width}×${media.height}` : undefined,
    media.durationMs ? `${Math.round(media.durationMs / 100) / 10}s` : undefined,
    media.byteSize ? formatBytes(media.byteSize) : undefined,
  ].filter(Boolean);
  return facts.length > 0 ? facts.join(' · ') : 'Metadata not supplied by Meta';
}

function mediaFailureMessage(media: AdCreativeProviderMediaReference): string {
  switch (media.reasonCode) {
    case 'meta_permission_denied':
      return 'Meta denied access to the file for the currently connected account.';
    case 'media_mirror_failed':
    case 'media_fetch_failed':
      return 'the provider file could not be downloaded; a later sync may recover it.';
    case 'meta_connection_expired':
      return 'the Meta connection expired before the file could be read.';
    case 'meta_video_source_unavailable':
    case 'provider_media_url_unavailable':
      return 'Meta did not expose a downloadable source rendition.';
    case 'media_content_length_exceeds_limit':
    case 'media_stream_exceeds_limit':
      return 'the file exceeds the configured import limit.';
    case 'media_mime_not_allowed':
    case 'media_magic_mismatch':
      return 'the returned file was not a valid supported image or video.';
    default:
      if (media.retrievalState === 'permission_denied') {
        return 'Meta denied access to the file for the currently connected account.';
      }
      if (media.retrievalState === 'expired') return 'the provider link expired before download.';
      if (media.retrievalState === 'too_large') return 'the file exceeds the configured import limit.';
      if (media.retrievalState === 'metadata_only') return 'Meta returned metadata only, without a downloadable file.';
      return 'the file was not available during this sync.';
  }
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 102.4) / 10} KB`;
  return `${Math.round(value / (1024 * 102.4)) / 10} MB`;
}
